import {
  ControllerRenderProps,
  FieldValues,
  Path,
  useFormContext,
} from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./form";
import { RequiredAsterisk } from "./required-asterisk";
import { Checkbox } from "../ui/checkbox";
import { CheckedState } from "@radix-ui/react-checkbox";

interface FieldCheckboxProps<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  label: string;
  options: {
    value: TFormValues[keyof TFormValues];
    label: string;
  }[];
  isDisabled?: boolean;
  hasRequiredAsterisk?: boolean;
  description?: React.ReactNode;
  containerClassName?: string;
  optionsDisable?: string[];
}
export function FieldCheckbox<TFormValues extends FieldValues>({
  name,
  label,
  options,
  isDisabled = false,
  hasRequiredAsterisk = false,
  description,
  containerClassName,
  optionsDisable = [],
}: FieldCheckboxProps<TFormValues>) {
  const form = useFormContext();

  const onCheckedChange = (
    checked: CheckedState,
    field: ControllerRenderProps<FieldValues, Path<TFormValues>>,
    option: {
      value: TFormValues[keyof TFormValues];
      label: string;
    },
  ) => {
    const value = field.value ? field.value : [];
    return checked
      ? field.onChange([...value, option.value])
      : field.onChange(value?.filter((v: string) => v !== option.value));
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={() => (
        <FormItem>
          <div className="mb-4">
            <FormLabel className="text-base">{label}</FormLabel>
            {hasRequiredAsterisk && <RequiredAsterisk />}
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </div>
          <div
            className={
              containerClassName ? containerClassName : "flex flex-col gap-y-2"
            }
          >
            {options.map((option) => (
              <FormField
                key={option.label}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem
                    key={option.label}
                    className="max-h- flex flex-row items-start space-x-3 space-y-0"
                  >
                    <FormControl>
                      <Checkbox
                        checked={field.value?.includes(option.value)}
                        onCheckedChange={(checked) => {
                          onCheckedChange(checked, field, option);
                        }}
                        disabled={
                          isDisabled ||
                          optionsDisable?.includes(String(option.value))
                        }
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {option.label}
                    </FormLabel>
                  </FormItem>
                )}
              />
            ))}
          </div>
        </FormItem>
      )}
    />
  );
}
